import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormHelperText, Typography, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.white,
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish5}`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'grid',
    gridTemplateColumns: 'fit-content(0) 1fr',
    alignItems: 'center',
    gap: 8,
    padding: spacing(2, 4),
    transition: 'border-color .3s ease',
    height: 56,
    '&:hover': {
      borderColor: primary.bluish2,
    },
    '&$selected:hover': {
      borderColor: primary.blue1,
    },
  },
  label: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 18,
    lineHeight: '20px',
    marginBottom: spacing(4),
  },
  content: {
    color: primary.bluish5,
    transition: 'color .3s ease',
    userSelect: 'none',
  },
  contentHighlighted: {
    color: primary.bluish1,
  },
  selected: {
    borderColor: primary.blue1,
  },
  error: {
    borderColor: primary.red1,
  },
  icon: {
    '& path': {
      transition: 'fill .3s ease',
      fill: primary.bluish5,
    },
  },
  iconActive: {
    '& path': {
      fill: primary.bluish1,
    },
  },
}));

const SettingsSummaryField = ({
  translations,
  value = {},
  settingsKey,
  settingsValueCount = 0,
  hasError = false,
  errorMessage = '',
  icon: Icon,
  isSubSetting = false,
  onSelect,
}) => {
  const classes = useStyles();

  const isSelected = isSubSetting
    ? value?.sub === settingsKey
    : value?.main === settingsKey;

  const content = settingsValueCount
    ? translations.contentWithValue.replace('[COUNT]', settingsValueCount)
    : translations.content;

  const handleSelect = () => {
    if (!isSelected) {
      onSelect({
        ...value,
        ...(isSubSetting ? { sub: settingsKey } : { main: settingsKey }),
      });
    }
  };

  return (
    <div>
      {translations.label && (
        <FormHelperText classes={{ root: classes.label }}>
          {translations.label}
        </FormHelperText>
      )}
      <div
        className={classNames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: hasError && !isSelected,
        })}
        onClick={handleSelect}
      >
        <Icon
          className={classNames(classes.icon, {
            [classes.iconActive]: !!settingsValueCount,
          })}
        />
        <Typography
          className={classNames(classes.content, {
            [classes.contentHighlighted]: !!settingsValueCount,
          })}
          variant="subtitle1"
        >
          {content}
        </Typography>
      </div>
      {hasError && (
        <FormHelperText error={hasError}>
          {errorMessage || translations.error}
        </FormHelperText>
      )}
    </div>
  );
};

SettingsSummaryField.propTypes = {
  translations: PropTypes.object.isRequired,
  settingsKey: PropTypes.string.isRequired,
  value: PropTypes.object,
  hasError: PropTypes.bool,
  settingsValueCount: PropTypes.number,
  errorMessage: PropTypes.string,
  icon: PropTypes.object.isRequired,
  isSubSetting: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

export default SettingsSummaryField;
